import React from "react";
import { convertDateToMoment } from "@/helper/dateHelper";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { MgtOrderDailyCountDto, SearchDateTimeRange } from "@/api/swagger";
import { AxiosResponse } from "axios";
import AnalysisWrapper from "@/components/Feature/AnalysisWrapper";

export type ReservationAnalysisSearchOrderDailyCountApi = (config: {
  time: SearchDateTimeRange;
}) => Promise<AxiosResponse<Array<MgtOrderDailyCountDto>>>;

interface Props {
  displayTitle?: boolean;
  searchOrderDailyCountApi: ReservationAnalysisSearchOrderDailyCountApi;
  type?: "bar" | "area";
}

const tickFormatter = (value: string) => {
  const weekDaysName = ["週日", "週一", "週二", "週三", "週四", "週五", "週六"];
  const moment = convertDateToMoment(new Date(value));

  return `${moment.format("MM/DD")} ${weekDaysName[moment.day()]}`;
};

const tooltipLabel = {
  cntCanceled: "取消",
  cntNoShow: "No-Show",
  cntCheckedIn: "已報到",
};

const tooltipFormatter = (value: number, name: keyof typeof tooltipLabel) => {
  return [value, tooltipLabel?.[name] ?? ""];
};

const Tick: React.FC<{ x: number; y: number; payload: { value: string } }> = ({
  x,
  y,
  payload,
}) => {
  const weekDaysName = ["週日", "週一", "週二", "週三", "週四", "週五", "週六"];
  const moment = convertDateToMoment(new Date(payload.value));

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor={"middle"} fill="#666">
        <tspan x="0" dy="1.2em">
          {moment.format("MM/DD")}
        </tspan>
        <tspan x="0" dy="1.2em">
          {weekDaysName[moment.day()]}
        </tspan>
      </text>
    </g>
  );
};

const COLORS: Record<
  NonNullable<Props["type"]>,
  Array<{ color: string; label: string }>
> = {
  area: [
    { color: "#B5DFC4", label: "已報到" },
    { color: "#B8B5E6", label: "取消" },
    { color: "#FEDE9F", label: "No-Show" },
  ],
  bar: [
    { color: "#00A2AD", label: "已報到" },
    { color: "#9FA1A7", label: "取消" },
    { color: "#4A4E57", label: "No-Show" },
  ],
};

const AreaChartView: React.FC<{
  data: Array<MgtOrderDailyCountDto>;
  xTickInterval?: number;
}> = ({ data, xTickInterval }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        width={500}
        height={400}
        data={data}
        margin={{
          top: 10,
          right: 10,
          left: -20,
          bottom: 20,
        }}
      >
        <CartesianGrid strokeDasharray={"3 3"} />
        <XAxis
          dataKey={"date"}
          interval={xTickInterval}
          tickFormatter={tickFormatter}
          tick={(props: any) => <Tick {...props} />}
        />
        <YAxis />
        <Tooltip formatter={tooltipFormatter} />
        <Area
          type={"monotone"}
          dataKey={"cntCheckedIn"}
          stackId={"1"}
          stroke={"#82ca9d"}
          fill={"#82ca9d"}
        />
        <Area
          type={"monotone"}
          dataKey={"cntCanceled"}
          stackId={"1"}
          stroke={"#8884d8"}
          fill={"#8884d8"}
        />
        <Area
          type={"monotone"}
          dataKey={"cntNoShow"}
          stackId={"1"}
          stroke={"#ffc658"}
          fill={"#ffc658"}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const BarChartView: React.FC<{
  data: Array<MgtOrderDailyCountDto>;
  xTickInterval?: number;
}> = ({ data, xTickInterval }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        width={500}
        height={400}
        data={data}
        margin={{
          top: 10,
          right: 10,
          left: -20,
          bottom: 20,
        }}
      >
        <CartesianGrid strokeDasharray={"3 3"} />
        <XAxis
          dataKey={"date"}
          interval={xTickInterval}
          tickFormatter={tickFormatter}
          tick={(props: any) => <Tick {...props} />}
        />
        <YAxis />
        <Tooltip formatter={tooltipFormatter} />
        <Bar dataKey={"cntCheckedIn"} stackId={"1"} fill={"#00A2AD"} />
        <Bar dataKey={"cntCanceled"} stackId={"1"} fill={"#9FA1A7"} />
        <Bar dataKey={"cntNoShow"} stackId={"1"} fill={"#4A4E57"} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const ReservationAnalysis: React.FC<Props> = ({
  displayTitle,
  searchOrderDailyCountApi,
  type = "area",
}) => {
  return (
    <AnalysisWrapper
      title={displayTitle ? "預約分析" : undefined}
      unit={"件"}
      legends={COLORS[type]}
      searchApi={searchOrderDailyCountApi}
      chart={({ data, xTickInterval }) => {
        if (type === "area") {
          return <AreaChartView data={data} xTickInterval={xTickInterval} />;
        } else if (type === "bar") {
          return <BarChartView data={data} xTickInterval={xTickInterval} />;
        } else {
          return null;
        }
      }}
    />
  );
};

export default ReservationAnalysis;
